import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  Loader2, RefreshCw, Plus, Save, Trash2, Upload, Download, Edit3,
  AlertCircle, AlertTriangle, Eye, EyeOff, FolderPlus, Database, CheckCircle2,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const API = window.location.origin + "/api";

const fmtEuro = (v) => {
  const n = Number(v || 0);
  try { return n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  catch { return n.toFixed(2); }
};

export default function TreatmentsCatalogPage() {
  const [listini, setListini] = useState([]);
  const [selectedListino, setSelectedListino] = useState("");
  const [treatments, setTreatments] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingTr, setLoadingTr] = useState(false);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef(null);
  const [editingCell, setEditingCell] = useState(null); // { treatmentId, field }
  const [newItem, setNewItem] = useState({ code: "", name: "", cost: 0, category: "" });
  const [cleanupBaseOpen, setCleanupBaseOpen] = useState(false);
  const [cleanupBaseReport, setCleanupBaseReport] = useState(null);
  const [cleanupBaseLoading, setCleanupBaseLoading] = useState(false);
  const [createListinoOpen, setCreateListinoOpen] = useState(false);
  const [newListinoName, setNewListinoName] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  // Import in blocco da Google Sheets (richiesta utente 04/06/2026):
  // ripristina il vecchio bottone con conferma (REPLACE totale).
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);

  const fetchListini = async () => {
    setLoadingList(true);
    try {
      const { data } = await axios.get(`${API}/admin/treatments-catalog/listini`, { withCredentials: true });
      // Ordinamento richiesto utente 04/06/2026:
      // 1) "BDD" sempre in testa
      // 2) gli altri listini con prefisso numerico (es. "21 - Allianz",
      //    "8 - Fasdac") in ordine progressivo del numero
      // 3) eventuali altri (senza prefisso numerico) in fondo alfabetici
      const sorted = [...(data.listini || [])].sort((a, b) => {
        // Ordine: BDD primo, poi visibili (numerico asc), poi nascosti in fondo
        if (a.hidden !== b.hidden) return a.hidden ? 1 : -1;
        if (a.listino === "BDD") return -1;
        if (b.listino === "BDD") return 1;
        const ma = a.listino.match(/^(\d+)\s*-/);
        const mb = b.listino.match(/^(\d+)\s*-/);
        if (ma && mb) return parseInt(ma[1], 10) - parseInt(mb[1], 10);
        if (ma) return -1;
        if (mb) return 1;
        return a.listino.localeCompare(b.listino, "it");
      });
      setListini(sorted);
      if (!selectedListino && sorted.length > 0) {
        // Pre-seleziona BDD se popolato, altrimenti il primo VISIBILE
        const visible = sorted.filter((l) => !l.hidden);
        const bddOrFirst = visible.find((l) => l.listino === "BDD" && l.count > 0)
          || visible.find((l) => l.count > 0)
          || visible[0]
          || sorted[0];
        setSelectedListino(bddOrFirst.listino);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento listini");
    } finally {
      setLoadingList(false);
    }
  };

  const fetchTreatments = async () => {
    if (!selectedListino) {
      setTreatments([]);
      return;
    }
    setLoadingTr(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/treatments-catalog/${encodeURIComponent(selectedListino)}`,
        { withCredentials: true },
      );
      setTreatments(data.treatments || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento prestazioni");
    } finally {
      setLoadingTr(false);
    }
  };

  useEffect(() => { fetchListini(); }, []); // eslint-disable-line
  useEffect(() => { fetchTreatments(); }, [selectedListino]); // eslint-disable-line

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return treatments;
    return treatments.filter(
      (t) =>
        t.code.toLowerCase().includes(q) ||
        t.name.toLowerCase().includes(q) ||
        (t.category || "").toLowerCase().includes(q),
    );
  }, [treatments, search]);

  // Lista univoca delle categorie esistenti per il dropdown
  // (richiesta utente 04/06/2026). Ordinate alfabeticamente.
  const categoryOptions = useMemo(() => {
    const set = new Set();
    treatments.forEach((t) => {
      const c = (t.category || "").trim().toLowerCase();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "it"));
  }, [treatments]);

  const handleSaveEdit = async (treatmentId, field, value) => {
    try {
      const body = {};
      if (field === "cost") body.cost = Number(value);
      else if (field === "name") body.name = value;
      else if (field === "category") body.category = value;
      else if (field === "price_editable") body.price_editable = !!value;
      else if (field === "code") {
        const normalized = String(value || "").trim().toUpperCase();
        if (!normalized) {
          toast.error("Il codice non può essere vuoto");
          setEditingCell(null);
          return;
        }
        body.code = normalized;
      }
      await axios.put(
        `${API}/admin/treatments-catalog/by-id/${encodeURIComponent(treatmentId)}`,
        body,
        { withCredentials: true },
      );
      toast.success("Prestazione aggiornata");
      setEditingCell(null);
      fetchTreatments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    }
  };

  // Salvataggio prezzo con opzione "Già inserite" → propagazione a tutte
  // le opp esistenti (Mongo + GHL monetaryValue + note_1).
  // Se `propagate=true`, prima mostra un modal di conferma con il count.
  const [propagateModal, setPropagateModal] = useState(null); // { treatmentId, newCost, oldCost, code, preview, loading }

  const handleSavePrice = async (treatmentId, newCost, propagate, oldCost, code) => {
    if (!propagate) {
      await handleSaveEdit(treatmentId, "cost", newCost);
      return;
    }
    // Open modal: fetch preview count first
    setPropagateModal({ treatmentId, newCost, oldCost, code, preview: null, loading: true, applying: false });
    try {
      const { data } = await axios.get(
        `${API}/admin/treatments-catalog/by-id/${encodeURIComponent(treatmentId)}/affected-opps`,
        { withCredentials: true },
      );
      setPropagateModal((m) => m ? { ...m, preview: data, loading: false } : m);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento preview");
      setPropagateModal(null);
    }
  };

  const confirmPropagateApply = async () => {
    if (!propagateModal) return;
    setPropagateModal((m) => ({ ...m, applying: true }));
    try {
      const { data } = await axios.put(
        `${API}/admin/treatments-catalog/by-id/${encodeURIComponent(propagateModal.treatmentId)}`,
        { cost: Number(propagateModal.newCost), propagate_to_existing: true },
        { withCredentials: true },
      );
      const p = data?.propagation;
      if (p?.skipped) {
        toast.info(p.reason || "Prezzo invariato, nessuna propagazione");
      } else if (p) {
        const errs = p.errors?.length || 0;
        toast.success(
          `Prezzo aggiornato. ${p.opps_updated}/${p.opps_found} opportunità riallineate${errs ? ` (${errs} errori)` : ""}.`,
        );
      } else {
        toast.success("Prezzo aggiornato");
      }
      setPropagateModal(null);
      setEditingCell(null);
      fetchTreatments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore propagazione");
      setPropagateModal((m) => m ? { ...m, applying: false } : m);
    }
  };

  const handleDelete = async (treatmentId, code) => {
    if (!window.confirm(`Eliminare la prestazione ${code}?`)) return;
    try {
      await axios.delete(
        `${API}/admin/treatments-catalog/by-id/${encodeURIComponent(treatmentId)}`,
        { withCredentials: true },
      );
      toast.success("Prestazione eliminata");
      fetchTreatments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const handleAdd = async () => {
    if (!newItem.code.trim() || !newItem.name.trim()) {
      toast.error("Codice e nome obbligatori");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/treatments-catalog/${encodeURIComponent(selectedListino)}`,
        {
          code: newItem.code.trim(),
          name: newItem.name.trim(),
          cost: Number(newItem.cost || 0),
          category: (newItem.category || "").trim().toLowerCase(),
        },
        { withCredentials: true },
      );
      toast.success("Prestazione aggiunta");
      setNewItem({ code: "", name: "", cost: 0, category: "" });
      setAddOpen(false);
      fetchTreatments();
      fetchListini();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  // Cleanup listino legacy "Base": eseguiamo prima un dry-run per
  // mostrare cosa verrà cancellato (record duplicati su BDD o orfani)
  // e cosa NO (codici referenziati in opportunità ma non in BDD).
  const handleCleanupBaseCheck = async () => {
    setCleanupBaseOpen(true);
    setCleanupBaseLoading(true);
    setCleanupBaseReport(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/treatments-catalog/cleanup-base`,
        {},
        { withCredentials: true, timeout: 120000 },
      );
      setCleanupBaseReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore verifica");
      setCleanupBaseOpen(false);
    } finally {
      setCleanupBaseLoading(false);
    }
  };

  const handleCleanupBaseConfirm = async () => {
    setCleanupBaseLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/treatments-catalog/cleanup-base?confirm=1`,
        {},
        { withCredentials: true, timeout: 120000 },
      );
      setCleanupBaseReport(data);
      toast.success(`Cancellati ${data.deleted} record dal listino "Base"`);
      fetchListini();
      if (selectedListino === "Base") {
        setSelectedListino("BDD");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore cancellazione");
    } finally {
      setCleanupBaseLoading(false);
    }
  };

  const hasLegacyBase = listini.some((l) => l.listino === "Base" && l.count > 0);
  const visibleListini = listini.filter((l) => !l.hidden);
  const hiddenListini = listini.filter((l) => l.hidden);

  // Crea nuovo listino vuoto (richiesta utente 04/06/2026)
  const handleCreateListino = async () => {
    const name = (newListinoName || "").trim();
    if (!name) {
      toast.error("Nome listino obbligatorio");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/treatments-catalog/listini`,
        { name },
        { withCredentials: true },
      );
      toast.success(`Listino "${name}" creato`);
      setNewListinoName("");
      setCreateListinoOpen(false);
      await fetchListini();
      setSelectedListino(name);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione listino");
    }
  };

  // Nascondi/mostra listino (toggle). Listino nascosto = disattivato
  // ovunque, anche nel form Piano di Cura.
  const handleToggleHidden = async (listino, nextHidden) => {
    try {
      await axios.put(
        `${API}/admin/treatments-catalog/listini/${encodeURIComponent(listino)}/hidden`,
        { hidden: nextHidden },
        { withCredentials: true },
      );
      toast.success(nextHidden ? `Listino "${listino}" nascosto` : `Listino "${listino}" riattivato`);
      // Se ho appena nascosto quello attivo, sposta selezione su BDD
      if (nextHidden && selectedListino === listino) {
        setSelectedListino("BDD");
      }
      fetchListini();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  // Import in blocco da Google Sheets (REPLACE totale di TUTTI i listini).
  // Mantiene fedelmente i codici duplicati presenti nei fogli.
  const handleMigrate = async () => {
    setMigrating(true);
    setMigrateResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/treatments-catalog/migrate-from-sheets`,
        {},
        { withCredentials: true, timeout: 300000 },
      );
      setMigrateResult(data);
      toast.success("Importazione completata");
      fetchListini();
      fetchTreatments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore importazione");
    } finally {
      setMigrating(false);
    }
  };

  // Export CSV del listino corrente (per backup). Formato compatibile
  // con `import-csv`: lo stesso file può essere ricaricato per ripristino.
  const handleExport = async () => {
    if (!selectedListino) return;
    setExporting(true);
    try {
      const resp = await axios.get(
        `${API}/admin/treatments-catalog/${encodeURIComponent(selectedListino)}/export-csv`,
        { withCredentials: true, responseType: "blob" },
      );
      const today = new Date().toISOString().slice(0, 10);
      const safe = selectedListino.replace(/\s+/g, "_").replace(/\//g, "_");
      const filename = `listino_${safe}_${today}.csv`;
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Esportato ${selectedListino}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore export");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(
        `${API}/admin/treatments-catalog/${encodeURIComponent(selectedListino)}/import-csv`,
        form,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportResult(data);
      toast.success(`Import completato: +${data.added} nuovi, ${data.updated} aggiornati`);
      fetchTreatments();
      fetchListini();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore import");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa] bg-grid-light pt-[calc(7px+3.5rem+13px)]">
      <Toaster position="top-right" theme="light" />
      <div className="w-full px-4 lg:px-[100px] pb-8">
        <PageHeader
          title="Gestione Listino"
          subtitle="Gestisci prezzi, prestazioni e listini assicurativi. I prezzi delle opportunità già inserite NON cambiano."
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setMigrateOpen(true)}
                data-testid="open-migrate-btn"
                className="bg-white/15 hover:bg-white/25 border-white/30 text-white"
                title="Importa in blocco tutti i listini dal Google Sheet (REPLACE totale)"
              >
                <Database className="w-4 h-4 mr-2" />
                Importa da Google Sheets
              </Button>
            </div>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 -mt-8 relative z-10">
          {/* Sidebar: lista listini */}
          <div className="bg-white rounded-3xl p-4 shadow-md">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-700">Listini</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCreateListinoOpen(true)}
                  title="Crea nuovo listino"
                  className="p-1.5 rounded-md hover:bg-emerald-100 text-emerald-600"
                  data-testid="create-listino-btn"
                >
                  <FolderPlus className="w-4 h-4" />
                </button>
                <button
                  onClick={fetchListini}
                  disabled={loadingList}
                  title="Aggiorna lista"
                  className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
                  data-testid="refresh-listini-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingList ? "animate-spin" : ""}`} />
                </button>
              </div>
            </div>
            {loadingList ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : (
              <div className="space-y-1 max-h-[70vh] overflow-y-auto">
                {visibleListini.map((l) => (
                  <ListinoRow
                    key={l.listino}
                    listino={l}
                    selected={selectedListino === l.listino}
                    onSelect={() => setSelectedListino(l.listino)}
                    onToggleHidden={() => handleToggleHidden(l.listino, true)}
                  />
                ))}
                {hiddenListini.length > 0 && (
                  <div className="pt-2 mt-2 border-t border-slate-200">
                    <button
                      onClick={() => setShowHidden(!showHidden)}
                      className="w-full text-left px-2 py-1 text-[11px] uppercase tracking-wider font-bold text-slate-400 hover:text-slate-600 flex items-center gap-1.5"
                      data-testid="toggle-hidden-section"
                    >
                      <EyeOff className="w-3 h-3" />
                      Nascosti ({hiddenListini.length})
                      <span className="ml-auto">{showHidden ? "▾" : "▸"}</span>
                    </button>
                    {showHidden && hiddenListini.map((l) => (
                      <ListinoRow
                        key={l.listino}
                        listino={l}
                        selected={selectedListino === l.listino}
                        onSelect={() => setSelectedListino(l.listino)}
                        onToggleHidden={() => handleToggleHidden(l.listino, false)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Legacy "Base" cleanup: appare solo se compare ancora il
                vecchio listino "Base". Apre dialog con check di sicurezza. */}
            {hasLegacyBase && (
              <button
                onClick={handleCleanupBaseCheck}
                data-testid="cleanup-base-btn"
                className="w-full mt-3 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 text-xs font-semibold transition-colors flex items-center gap-2"
                title="Verifica e cancella il listino legacy 'Base' (sostituito da BDD)"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Elimina listino &quot;Base&quot; (legacy)
              </button>
            )}
          </div>

          {/* Main: tabella prestazioni */}
          <div className="bg-white rounded-3xl p-4 sm:p-6 shadow-md">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <h2 className="text-lg font-bold text-slate-800 flex-1 min-w-[200px]">
                {selectedListino || "Seleziona un listino"}
                {treatments.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-slate-500">
                    ({filtered.length}/{treatments.length} prestazioni)
                  </span>
                )}
                {selectedListino && listini.find((l) => l.listino === selectedListino)?.hidden && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                    <EyeOff className="w-3 h-3" /> Nascosto
                  </span>
                )}
              </h2>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca codice, nome o categoria…"
                className="w-64 h-10"
                data-testid="search-treatments"
                disabled={!selectedListino || loadingTr}
              />
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={!selectedListino || exporting || treatments.length === 0}
                data-testid="export-csv-btn"
                title="Scarica il listino corrente come CSV (formato compatibile con 'Carica CSV')"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Esporta CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={!selectedListino || importing}
                data-testid="upload-csv-btn"
                title="Carica un CSV con header: Codice Prestazione, Prestazioni, Costo, Categorie"
              >
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Carica CSV
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={handleImport}
                data-testid="csv-file-input"
              />
              <Button
                onClick={() => setAddOpen(true)}
                disabled={!selectedListino}
                className="bg-[#3B42DD] hover:bg-[#2f36b8] text-white"
                data-testid="add-treatment-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Nuova prestazione
              </Button>
            </div>

            {!selectedListino ? (
              <div className="text-center py-16 text-slate-400">
                Seleziona un listino dalla sidebar.
              </div>
            ) : loadingTr ? (
              <div className="text-center py-16">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-400 space-y-3">
                {treatments.length === 0 ? (
                  <>
                    <AlertCircle className="w-10 h-10 mx-auto text-amber-400" />
                    <div className="text-sm">Listino vuoto. Usa &quot;Carica CSV&quot; o &quot;Nuova prestazione&quot; per popolarlo.</div>
                  </>
                ) : (
                  <div className="text-sm">Nessun risultato per &quot;{search}&quot;</div>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="treatments-table">
                  <thead className="text-xs text-slate-500 bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Codice</th>
                      <th className="text-left px-3 py-2 font-semibold">Nome</th>
                      <th className="text-right px-3 py-2 font-semibold">Prezzo (€)</th>
                      <th className="text-left px-3 py-2 font-semibold">Categoria</th>
                      <th
                        className="text-center px-3 py-2 font-semibold"
                        title="Se attivo, gli utenti con flag 'Modifica prezzo' possono sovrascrivere il prezzo a mano nel piano di cura (qualunque stage)"
                      >
                        Prezzo<br />modificabile
                      </th>
                      <th className="text-left px-3 py-2 font-semibold">Ultimo aggiornamento</th>
                      <th className="text-right px-3 py-2 font-semibold w-24">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((t) => (
                      <tr key={t.treatment_id || t.code} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`row-${t.treatment_id || t.code}`}>
                        <td className="px-3 py-2 font-mono font-semibold text-slate-700">
                          <EditableCell
                            value={t.code}
                            isEditing={editingCell?.treatmentId === t.treatment_id && editingCell?.field === "code"}
                            onStartEdit={() => setEditingCell({ treatmentId: t.treatment_id, field: "code" })}
                            onCancel={() => setEditingCell(null)}
                            onSave={(v) => handleSaveEdit(t.treatment_id, "code", v)}
                          />
                        </td>
                        <td className="px-3 py-2 text-slate-800">
                          <EditableCell
                            value={t.name}
                            isEditing={editingCell?.treatmentId === t.treatment_id && editingCell?.field === "name"}
                            onStartEdit={() => setEditingCell({ treatmentId: t.treatment_id, field: "name" })}
                            onCancel={() => setEditingCell(null)}
                            onSave={(v) => handleSaveEdit(t.treatment_id, "name", v)}
                          />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">
                          <PriceEditCell
                            value={t.cost}
                            isEditing={editingCell?.treatmentId === t.treatment_id && editingCell?.field === "cost"}
                            onStartEdit={() => setEditingCell({ treatmentId: t.treatment_id, field: "cost" })}
                            onCancel={() => setEditingCell(null)}
                            onSave={(newCost, propagate) => handleSavePrice(t.treatment_id, newCost, propagate, t.cost, t.code)}
                            code={t.code}
                          />
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          <EditableCell
                            value={t.category}
                            type="select"
                            options={categoryOptions}
                            isEditing={editingCell?.treatmentId === t.treatment_id && editingCell?.field === "category"}
                            onStartEdit={() => setEditingCell({ treatmentId: t.treatment_id, field: "category" })}
                            onCancel={() => setEditingCell(null)}
                            onSave={(v) => handleSaveEdit(t.treatment_id, "category", v)}
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={!!t.price_editable}
                            onChange={(e) => handleSaveEdit(t.treatment_id, "price_editable", e.target.checked)}
                            data-testid={`price-editable-${t.treatment_id || t.code}`}
                            title="Prezzo modificabile a mano nel piano di cura (richiede flag utente 'Modifica prezzo')"
                            className="w-4 h-4 accent-sky-600 cursor-pointer"
                          />
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          <div>{fmtDate(t.updated_at)}</div>
                          {t.updated_by && <div className="text-slate-400 truncate max-w-[160px]" title={t.updated_by}>{t.updated_by}</div>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => handleDelete(t.treatment_id, t.code)}
                            data-testid={`delete-${t.treatment_id || t.code}`}
                            title="Elimina prestazione"
                            className="p-1.5 rounded hover:bg-red-50 text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {importResult && (
              <div className="mt-4 p-3 rounded-lg border border-emerald-200 bg-emerald-50 text-xs text-emerald-900">
                <strong>Import completato:</strong> +{importResult.added} nuove · {importResult.updated} aggiornate · {importResult.unchanged} invariate · {importResult.skipped} saltate
                {importResult.errors?.length > 0 && (
                  <div className="text-red-700 mt-1">{importResult.errors.length} errori</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Add dialog */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent data-testid="add-treatment-dialog">
            <DialogHeader>
              <DialogTitle>Nuova prestazione</DialogTitle>
              <DialogDescription className="text-xs">
                Aggiungi una prestazione al listino <strong>{selectedListino}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold">Codice *</Label>
                <Input
                  value={newItem.code}
                  onChange={(e) => setNewItem({ ...newItem, code: e.target.value.toUpperCase() })}
                  placeholder="ABT"
                  data-testid="new-tr-code"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Nome *</Label>
                <Input
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  placeholder="Ablazione tartaro"
                  data-testid="new-tr-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Prezzo (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newItem.cost}
                    onChange={(e) => setNewItem({ ...newItem, cost: e.target.value })}
                    data-testid="new-tr-cost"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Categoria</Label>
                  <input
                    list={`cat-options-${selectedListino}`}
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    placeholder="igiene"
                    data-testid="new-tr-category"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                  <datalist id={`cat-options-${selectedListino}`}>
                    {categoryOptions.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Annulla</Button>
              <Button onClick={handleAdd} className="bg-[#3B42DD] hover:bg-[#2f36b8]" data-testid="confirm-add-btn">
                <Save className="w-4 h-4 mr-2" /> Aggiungi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Crea listino dialog */}
        <Dialog open={createListinoOpen} onOpenChange={setCreateListinoOpen}>
          <DialogContent data-testid="create-listino-dialog">
            <DialogHeader>
              <DialogTitle>Nuovo listino</DialogTitle>
              <DialogDescription className="text-xs">
                Crea un nuovo listino vuoto. Potrai aggiungere prestazioni manualmente o caricare un CSV.
              </DialogDescription>
            </DialogHeader>
            <div>
              <Label className="text-xs font-semibold">Nome listino *</Label>
              <Input
                value={newListinoName}
                onChange={(e) => setNewListinoName(e.target.value)}
                placeholder="es. 25 - Nuova Compagnia"
                data-testid="new-listino-name"
                onKeyDown={(e) => { if (e.key === "Enter") handleCreateListino(); }}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Suggerimento: per mantenere l&apos;ordinamento, usa il prefisso numerico (es. &quot;25 - Allianz Plus&quot;).
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateListinoOpen(false)}>Annulla</Button>
              <Button onClick={handleCreateListino} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-create-listino-btn">
                <FolderPlus className="w-4 h-4 mr-2" /> Crea
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cleanup legacy "Base" dialog */}
        <Dialog open={migrateOpen} onOpenChange={(o) => { setMigrateOpen(o); if (!o) setMigrateResult(null); }}>
          <DialogContent className="max-w-2xl" data-testid="migrate-dialog">
            <DialogHeader>
              <DialogTitle>Importa da Google Sheets</DialogTitle>
              <DialogDescription className="text-xs space-y-2 pt-2">
                <span className="block">
                  Scarica TUTTI i listini dal Google Sheet e li copia nel database.
                </span>
                <span className="block text-red-700 bg-red-50 border border-red-200 p-2 rounded">
                  <strong>REPLACE TOTALE</strong>: tutte le prestazioni attuali dei listini ufficiali verranno CANCELLATE
                  e sostituite con i dati del Google Sheet. I codici duplicati legittimi vengono mantenuti.
                  Esporta CSV prima di procedere se vuoi un backup.
                </span>
              </DialogDescription>
            </DialogHeader>
            {migrateResult ? (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                <div className="flex items-center gap-2 text-emerald-700 mb-2">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-semibold">Importazione completata</span>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-2 py-1">Listino</th>
                      <th className="text-right px-2 py-1">Prima</th>
                      <th className="text-right px-2 py-1">Dopo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(migrateResult?.listini || []).map((l) => (
                      <tr key={l?.listino || JSON.stringify(l)} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-medium">{l?.listino}</td>
                        {l?.error ? (
                          <td colSpan={2} className="px-2 py-1 text-red-600">{l.error}</td>
                        ) : (
                          <>
                            <td className="px-2 py-1 text-right text-slate-500 tabular-nums">{l?.before ?? "—"}</td>
                            <td className="px-2 py-1 text-right tabular-nums font-semibold text-emerald-700">{l?.after ?? l?.total ?? 0}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="outline" onClick={() => setMigrateOpen(false)} disabled={migrating}>Chiudi</Button>
              {!migrateResult && (
                <Button onClick={handleMigrate} disabled={migrating} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-migrate-btn">
                  {migrating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                  Avvia REPLACE
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cleanup legacy "Base" dialog */}
        <Dialog open={cleanupBaseOpen} onOpenChange={(o) => {
          setCleanupBaseOpen(o);
          if (!o) setCleanupBaseReport(null);
        }}>
          <DialogContent className="max-w-2xl" data-testid="cleanup-base-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                Elimina listino legacy &quot;Base&quot;
              </DialogTitle>
              <DialogDescription className="text-xs pt-2">
                Verifichiamo se le prestazioni del listino &quot;Base&quot; sono referenziate in piani di cura esistenti. I prezzi delle opp già inserite NON cambiano in ogni caso (sono snapshot).
              </DialogDescription>
            </DialogHeader>
            {cleanupBaseLoading && !cleanupBaseReport ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                <span className="ml-2 text-sm text-slate-500">Verifica in corso…</span>
              </div>
            ) : cleanupBaseReport ? (
              <div className="space-y-3 max-h-[55vh] overflow-y-auto">
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-slate-50 rounded p-2">
                    <div className="text-[10px] uppercase font-bold text-slate-400">Trovati</div>
                    <div className="text-lg font-bold tabular-nums">{cleanupBaseReport?.found ?? 0}</div>
                  </div>
                  <div className="bg-emerald-50 rounded p-2">
                    <div className="text-[10px] uppercase font-bold text-emerald-600">Cancellabili</div>
                    <div className="text-lg font-bold tabular-nums text-emerald-700">{cleanupBaseReport?.safe_to_delete_count ?? 0}</div>
                  </div>
                  <div className="bg-amber-50 rounded p-2">
                    <div className="text-[10px] uppercase font-bold text-amber-600">Attenzione</div>
                    <div className="text-lg font-bold tabular-nums text-amber-700">{cleanupBaseReport?.warnings_count ?? 0}</div>
                  </div>
                </div>

                {(cleanupBaseReport?.warnings_count ?? 0) > 0 && (
                  <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
                      <AlertTriangle className="w-4 h-4" /> Codici NON cancellati
                    </div>
                    <div className="text-xs text-amber-900">
                      Questi codici esistono solo nel listino Base e sono referenziati in opportunità.
                      Verranno mantenuti. Considera di migrarli manualmente nel listino BDD.
                    </div>
                    <table className="w-full text-xs">
                      <thead className="text-amber-700">
                        <tr>
                          <th className="text-left px-2 py-1">Codice</th>
                          <th className="text-left px-2 py-1">Nome</th>
                          <th className="text-right px-2 py-1">Opp ref.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(cleanupBaseReport?.warnings || []).map((w) => (
                          <tr key={w.code} className="border-t border-amber-200">
                            <td className="px-2 py-1 font-mono font-semibold">{w.code}</td>
                            <td className="px-2 py-1">{w.name}</td>
                            <td className="px-2 py-1 text-right tabular-nums font-semibold">{w.opp_references}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {(cleanupBaseReport?.found ?? 0) === 0 && (
                  <div className="border border-slate-200 bg-slate-50 rounded-lg p-3 text-xs text-slate-700">
                    ℹ️ Nessun record &quot;Base&quot; trovato. Il listino legacy è già stato eliminato.
                  </div>
                )}

                {(cleanupBaseReport?.safe_to_delete_count ?? 0) > 0 && cleanupBaseReport?.dry_run && (
                  <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 text-xs text-emerald-900">
                    <div className="font-semibold mb-1">✓ Pronti per la cancellazione</div>
                    <div>
                      {cleanupBaseReport.safe_to_delete_count} record &quot;Base&quot; sono safe (duplicati in BDD o orfani).
                      Clicca &quot;Conferma cancellazione&quot; per rimuoverli.
                    </div>
                  </div>
                )}

                {cleanupBaseReport && !cleanupBaseReport.dry_run && (
                  <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 text-xs text-emerald-900 font-semibold">
                    ✅ Cancellazione completata: {cleanupBaseReport.deleted ?? 0} record rimossi.
                  </div>
                )}
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCleanupBaseOpen(false)} disabled={cleanupBaseLoading}>
                Chiudi
              </Button>
              {cleanupBaseReport && cleanupBaseReport.dry_run && (cleanupBaseReport?.safe_to_delete_count ?? 0) > 0 && (
                <Button
                  onClick={handleCleanupBaseConfirm}
                  disabled={cleanupBaseLoading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-cleanup-base-btn"
                >
                  {cleanupBaseLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Conferma cancellazione ({cleanupBaseReport.safe_to_delete_count})
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal di conferma propagazione prezzo a opp esistenti */}
        <Dialog open={!!propagateModal} onOpenChange={(open) => { if (!open && !propagateModal?.applying) setPropagateModal(null); }}>
          <DialogContent data-testid="propagate-price-modal">
            <DialogHeader>
              <DialogTitle>Applica nuovo prezzo a opportunità esistenti?</DialogTitle>
              <DialogDescription>
                Stai per cambiare il prezzo della prestazione <strong>{propagateModal?.code}</strong> da{" "}
                <strong>{fmtEuro(propagateModal?.oldCost)}</strong> a{" "}
                <strong>{fmtEuro(propagateModal?.newCost)}</strong>. Vuoi propagare il nuovo prezzo anche alle opportunità già inserite per il listino <strong>{selectedListino}</strong>? Verranno aggiornati i campi prezzo singola prestazione + totale piano di cura sia su questo database sia su GHL (note_1 + monetaryValue).
              </DialogDescription>
            </DialogHeader>
            {propagateModal?.loading && (
              <div className="flex items-center gap-2 py-4 text-sm text-slate-600">
                <Loader2 className="w-4 h-4 animate-spin" /> Conteggio opportunità impattate…
              </div>
            )}
            {propagateModal?.preview && (
              <div className="space-y-3 text-sm">
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <div className="font-semibold text-amber-900">
                    {propagateModal.preview.opps_count === 0
                      ? "Nessuna opportunità impattata"
                      : `${propagateModal.preview.opps_count} opportunità impattate`}
                  </div>
                  <div className="text-xs text-amber-800 mt-1">
                    Sono escluse: opp in Archivio pratiche (cancellate da GHL), righe con priceChoice = bdd/manual e quelle in split a Indiretti.
                  </div>
                </div>
                {propagateModal.preview.sample?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr className="text-slate-600">
                          <th className="text-left px-2 py-1 font-semibold">Paziente</th>
                          <th className="text-left px-2 py-1 font-semibold">Assicurazione</th>
                          <th className="text-right px-2 py-1 font-semibold">Prezzo att.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {propagateModal.preview.sample.map((o) => (
                          <tr key={o.opportunity_id} className="border-t border-slate-100">
                            <td className="px-2 py-1 truncate max-w-[180px]" title={o.contact_name}>{o.contact_name || "—"}</td>
                            <td className="px-2 py-1 text-slate-600 truncate max-w-[140px]" title={o.assicurazione_name}>{o.assicurazione_name}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{fmtEuro(o.current_euro || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {propagateModal.preview.opps_count > propagateModal.preview.sample.length && (
                      <div className="text-xs text-slate-500 px-2 py-1 border-t border-slate-100">
                        … e altre {propagateModal.preview.opps_count - propagateModal.preview.sample.length} opportunità
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPropagateModal(null)} disabled={propagateModal?.applying}>
                Annulla
              </Button>
              <Button
                onClick={confirmPropagateApply}
                disabled={propagateModal?.loading || propagateModal?.applying || propagateModal?.preview?.opps_count === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="confirm-propagate-btn"
              >
                {propagateModal?.applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {propagateModal?.preview?.opps_count
                  ? `Applica a ${propagateModal.preview.opps_count} opportunità`
                  : "Conferma"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// Riga listino in sidebar: selezionabile + icona "nascondi/mostra" in hover.
function ListinoRow({ listino, selected, onSelect, onToggleHidden }) {
  const isHidden = !!listino.hidden;
  return (
    <div
      className={`group relative flex items-center rounded-lg transition-colors ${
        selected
          ? "bg-[#3B42DD] text-white"
          : isHidden
            ? "text-slate-400 hover:bg-slate-50"
            : "text-slate-700 hover:bg-slate-50"
      }`}
    >
      <button
        onClick={onSelect}
        data-testid={`listino-${listino.listino}`}
        className={`flex-1 flex items-center justify-between px-3 py-2 text-left text-sm min-w-0 ${
          selected ? "font-semibold" : ""
        }`}
      >
        <span className={`truncate ${isHidden ? "italic line-through decoration-slate-300" : ""}`}>
          {listino.listino}
        </span>
        <span className={`text-xs tabular-nums ml-2 ${
          selected ? "text-white/70" : (listino.count > 0 ? "text-slate-400" : "text-amber-500 font-bold")
        }`} title={listino.count === 0 ? "Listino vuoto" : ""}>
          {listino.count}
        </span>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onToggleHidden(); }}
        title={isHidden ? "Mostra listino" : "Nascondi listino (disattiva)"}
        data-testid={`toggle-hidden-${listino.listino}`}
        className={`px-2 py-2 mr-1 rounded transition-opacity ${
          selected
            ? "text-white/80 hover:text-white opacity-100"
            : "text-slate-400 hover:text-slate-700 opacity-0 group-hover:opacity-100"
        } ${isHidden ? "opacity-100" : ""}`}
      >
        {isHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
    </div>
  );
}

// Cell editabile inline (click → input → save su Enter / blur, cancel su Esc)
function EditableCell({ value, type = "text", options = [], isEditing, onStartEdit, onCancel, onSave, display }) {
  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={onStartEdit}
        className="text-left w-full hover:bg-slate-100 rounded px-1 -mx-1 py-0.5 group inline-flex items-center gap-1"
        title="Click per modificare"
      >
        <span>{display ? display(value) : (value || "—")}</span>
        <Edit3 className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
    );
  }

  // Per il campo "categoria" usiamo un <input list=…> nativo che combina
  // dropdown (con le opzioni esistenti) + libera digitazione (per creare
  // nuove categorie al volo). Richiesta utente 04/06/2026.
  if (type === "select") {
    return (
      <>
        <input
          autoFocus
          list="editcell-category-options"
          defaultValue={value || ""}
          onBlur={(e) => onSave(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); onSave(e.target.value); }
            if (e.key === "Escape") { e.preventDefault(); onCancel(); }
          }}
          className="border border-blue-300 rounded px-1.5 py-0.5 text-sm w-full bg-white outline-none focus:ring-2 focus:ring-blue-200"
          data-testid="editable-select"
        />
        <datalist id="editcell-category-options">
          {options.map((opt) => (
            <option key={opt} value={opt} />
          ))}
        </datalist>
      </>
    );
  }

  // L'input parte sempre con `defaultValue` (uncontrolled) così evitiamo
  // di sincronizzare state con prop via useEffect (regola eslint
  // react-hooks/set-state-in-effect). Save legge il valore dal target.
  return (
    <input
      autoFocus
      type={type}
      step={type === "number" ? "0.01" : undefined}
      defaultValue={value}
      onBlur={(e) => onSave(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); onSave(e.target.value); }
        if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      }}
      className="border border-blue-300 rounded px-1.5 py-0.5 text-sm w-full bg-white outline-none focus:ring-2 focus:ring-blue-200"
      data-testid="editable-input"
    />
  );
}


// Cell editabile dedicata al campo prezzo: input numero + checkbox
// "Già inserite" (propaga il nuovo prezzo a tutte le opp esistenti che
// usano questo codice nel listino corrente) + bottone "Salva".
// Non salva su blur (la checkbox blurra l'input e perderemmo il valore):
// save solo su Enter o click esplicito sul bottone. Esc annulla.
function PriceEditCell({ value, isEditing, onStartEdit, onCancel, onSave, code }) {
  const [draft, setDraft] = useState(value);
  const [propagate, setPropagate] = useState(false);

  // Reset state quando entriamo in edit mode
  useEffect(() => {
    if (isEditing) {
      setDraft(value);
      setPropagate(false);
    }
  }, [isEditing, value]);

  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={onStartEdit}
        className="text-left w-full hover:bg-slate-100 rounded px-1 -mx-1 py-0.5 group inline-flex items-center gap-1"
        title="Click per modificare"
        data-testid={`price-cell-${code}`}
      >
        <span>{fmtEuro(value)}</span>
        <Edit3 className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
    );
  }

  const commit = () => {
    const num = Number(draft);
    if (Number.isNaN(num)) {
      toast.error("Prezzo non valido");
      return;
    }
    onSave(num, propagate);
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5" data-testid={`price-edit-${code}`}>
      <input
        autoFocus
        type="number"
        step="0.01"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { e.preventDefault(); onCancel(); }
        }}
        className="border border-blue-300 rounded px-1.5 py-0.5 text-sm w-24 bg-white outline-none focus:ring-2 focus:ring-blue-200 tabular-nums text-right"
        data-testid={`price-input-${code}`}
      />
      <label
        className="inline-flex items-center gap-1 text-[11px] text-slate-700 cursor-pointer select-none whitespace-nowrap"
        title="Applica il nuovo prezzo anche alle opportunità già esistenti (Mongo + GHL)"
      >
        <input
          type="checkbox"
          checked={propagate}
          onChange={(e) => setPropagate(e.target.checked)}
          className="w-3.5 h-3.5 accent-amber-500 cursor-pointer"
          data-testid={`price-propagate-${code}`}
        />
        <span>Già inserite</span>
      </label>
      <button
        type="button"
        onClick={commit}
        className="px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold"
        title="Salva nuovo prezzo"
        data-testid={`price-save-${code}`}
      >
        Salva
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="px-1.5 py-0.5 rounded text-slate-400 hover:bg-slate-100 text-[11px]"
        title="Annulla"
        data-testid={`price-cancel-${code}`}
      >
        ✕
      </button>
    </div>
  );
}
